import React, { useMemo } from 'react'
import { Card, Table, Tag, Tooltip, Select } from 'antd'
import { ColumnProps } from 'antd/es/table'
import { useTableService } from '@hooks/useTableService'
import { getReleaseApprovalRecordList, exportReleaseApprovalRecordList } from '@services/customer'
import { ReleaseApprovalRecordItem, ReleaseProtectionRecordFilter } from '@types'
import { CommonFilters, PageHeader, TableDownload, UserAvatar } from '@components'
import { formatTime, trimStr } from '@library'
import { getSortOrder } from '@configs/table'
import { TableProps } from 'antd/lib/table'
import { Link } from 'react-router-dom'
import { FieldItem } from 'src/components/CommonFilters/configs'

// 审核状态映射
const statusMap = {
  PENDING: { text: '待审核', color: 'orange' },
  APPROVED: { text: '审核成功', color: 'green' },
  REJECTED: { text: '审核驳回', color: 'red' },
  RELEASED: { text: '释放成功', color: 'blue' },
  RELEASE_FAILED: { text: '释放失败', color: 'red' },
}

// 状态选项
const statusOptions = [
  { label: '待审核', value: 'PENDING' },
  { label: '审核成功', value: 'APPROVED' },
  { label: '审核驳回', value: 'REJECTED' },
  { label: '释放成功', value: 'RELEASED' },
  { label: '释放失败', value: 'RELEASE_FAILED' },
]

const initFilter: ReleaseProtectionRecordFilter = {
  page: 1,
  pageSize: 20,
}

export default function ReleaseProtectionRecord () {
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
  } = useTableService(getReleaseApprovalRecordList, initFilter, '获取释放保护期记录失败')

  // 表格排序处理
  const onTableChange: TableProps<ReleaseApprovalRecordItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<ReleaseProtectionRecordFilter>(sorter)
    setFilter(sort)
  }

  // 筛选项配置
  const filterItems: FieldItem[] = useMemo(() => [
    {
      fieldName: 'applyTime',
      label: '申请时间',
      width: 180,
      type: 'rangePicker',
      handleValue: (val: any) => ({
        applyTimeFrom: formatTime(val?.[0], 'start'),
        applyTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    { fieldName: 'customerName', label: '被释放客户名', type: 'customerNameSelect' },
    { fieldName: 'cid', label: 'CID', type: 'multiTags' },
    { fieldName: 'uin', label: 'UIN', type: 'multiTags' },
    {
      fieldName: 'status',
      label: '审核状态',
      render: () => (
        <Select
          placeholder="请选择审核状态"
          allowClear
          options={statusOptions}
        />
      ),
    },
  ], [filter.sort, filter.sortType])

  const columns: ColumnProps<ReleaseApprovalRecordItem>[] = useMemo(() => [
    {
      title: '申请时间',
      dataIndex: 'applyTime',
      key: 'applyTime',
      width: 180,
    },
    {
      title: '被释放客户名',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 200,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>),
    },
    {
      title: '被释放客户CID',
      dataIndex: 'cid',
      key: 'cid',
      width: 200,
    },
    {
      title: '被释放客户UIN',
      dataIndex: 'uin',
      key: 'uin',
      width: 150,
      render: (uin) => uin || '-',
    },
    {
      title: '原始发起方',
      dataIndex: 'originInitiatorRtx',
      key: 'originInitiatorRtx',
      width: 120,
    },
    {
      title: '关联渠道经理',
      dataIndex: 'channelManagerRtx',
      key: 'channelManagerRtx',
      width: 120,
      render: value => <UserAvatar salesName={value} />,
    },
    {
      title: '申请原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (reason) => (
        <Tooltip title={reason}>
          <span>{reason}</span>
        </Tooltip>
      ),
    },
    {
      title: '申请人',
      dataIndex: 'applicant',
      key: 'applicant',
      width: 100,
      render: value => <UserAvatar salesName={value} />,
    },
    {
      title: '审核状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusInfo = statusMap[status] || { text: status, color: 'default' }
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
      },
    },
  ], [filter.sort, filter.sortType])

  return (
    <>
      <PageHeader title="释放保护期记录" />
      <Card className="mt20 mb20">
        <CommonFilters
          className="custom-Label"
          colProps={{ flex: '1 0 300px' }}
          itemList={filterItems}
          standstill={9}
          onSubmit={(filterData) => {
            setFilter({ ...filterData, page: 1 })
          }}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card>
        <TableDownload className="ml20" exportMethod={exportReleaseApprovalRecordList} filter={filter} />
        <Table
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          rowKey="applyId"
          scroll={{ x: 1200 }}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}
